/**
 * QuestionRenderer Component
 * Unified component that renders any question type
 */

import React from 'react';
import { Box, Alert } from '@mui/material';
import type { Question, ResponseValue } from '../../types';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import SingleSelectRenderer from './renderers/SingleSelectRenderer';
import MultiSelectRenderer from './renderers/MultiSelectRenderer';
import FormEntryRenderer from './renderers/FormEntryRenderer';
import DateRenderer from './renderers/DateRenderer';
import DropdownRenderer from './renderers/DropdownRenderer';
import LinkRenderer from './renderers/LinkRenderer';
import VideoRenderer from './renderers/VideoRenderer';
import CustomerInsightsFormRenderer from './renderers/CustomerInsightsFormRenderer';
import AiProcessedRenderer from './renderers/AiProcessedRenderer';
import MailtoRenderer from './renderers/MailtoRenderer';

interface QuestionRendererProps {
  question: Question;
  selectedValue: any; // Can be number, number[], Record<string, string>, or AiProcessedValue
  onSelect: (value: any) => void;
  responses?: Record<number, ResponseValue>;
  questions?: Question[];
  onAiProcessingComplete?: () => void;
}

function QuestionRenderer({
  question,
  selectedValue,
  onSelect,
  responses = {},
  questions = [],
  onAiProcessingComplete,
}: QuestionRendererProps): JSX.Element {
  const renderQuestion = (): JSX.Element => {
    switch (question.type) {
      case 'SingleSelect':
        return (
          <SingleSelectRenderer
            question={question}
            selectedValue={selectedValue}
            onSelect={onSelect}
          />
        );

      case 'MultiSelect':
        return (
          <MultiSelectRenderer
            question={question}
            selectedValues={Array.isArray(selectedValue) ? selectedValue : []}
            onSelect={onSelect}
          />
        );

      case 'FormEntry':
        return (
          <FormEntryRenderer
            question={question}
            selectedValues={typeof selectedValue === 'object' && selectedValue !== null ? selectedValue : {}}
            onSelect={onSelect}
          />
        );

      case 'Date':
        return (
          <DateRenderer
            question={question}
            selectedValue={typeof selectedValue === 'string' ? selectedValue : undefined}
            onSelect={onSelect}
          />
        );

      case 'Dropdown':
        return (
          <DropdownRenderer
            question={question}
            selectedValues={typeof selectedValue === 'object' && selectedValue !== null ? selectedValue : {}}
            onSelect={onSelect}
          />
        );

      case 'Video':
      case 'Links':
        return (
          <Box>
            <VideoRenderer question={question} />
            <LinkRenderer question={question} />
          </Box>
        );

      case 'Information':
        return (
          <Box className="cla-quiz-information" sx={{ mt: 1, border: '1px solid', borderColor: 'divider', borderTop: '4px solid', borderTopColor: 'primary.main', borderRadius: 2, p: 3, backgroundColor: 'background.paper' }}>
            {question.metadata?.description && (
              <Box
                className="cla-quiz-information-content"
                sx={{ color: 'text.secondary', '& p:last-child': { mb: 0 } }}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(question.metadata.description) }}
              />
            )}
          </Box>
        );

      case 'CustomerInsightsForm':
        return (
          <CustomerInsightsFormRenderer
            question={question}
            selectedValues={typeof selectedValue === 'object' && selectedValue !== null ? selectedValue : {}}
            onSelect={onSelect}
            responses={responses}
            questions={questions}
          />
        );

      case 'AiProcessed':
        return (
          <AiProcessedRenderer
            question={question}
            selectedValue={selectedValue}
            onSelect={onSelect}
            onProcessingComplete={onAiProcessingComplete}
          />
        );

      case 'Mailto':
        return (
          <MailtoRenderer
            key={question.id}
            question={question}
            selectedValue={typeof selectedValue === 'object' && selectedValue !== null ? selectedValue : {}}
            onSelect={onSelect}
          />
        );

      default:
        return (
          <Alert severity="error">
            Unknown question type: {question.type}
          </Alert>
        );
    }
  };

  return (
    <Box>
      {renderQuestion()}
    </Box>
  );
}

export default QuestionRenderer;
